// ─────────────────────────────────────────────────────────────────────────────
// PostScanPage.tsx
//
// Page 3 of the demo flow: shows analysis results.
//
// For each strip detected, plots the intensity profile + fitted baseline as a
// line chart, then renders a small table of T/C ratios with a coloured "+/–"
// verdict per test line.
//
// The verdict thresholds (TC_RATIO_THRESHOLD, C_THRESHOLD) are illustrative.
// Real interpretation depends on the assay; tweak per reader if needed.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

import type { PmfStoryDiagnostics } from '@novarumdx/ndx-imaging-web';
import type { CompletedScan } from './exportResult';
import { downloadScanZip, downloadAllScanZips } from './exportResult';
import PmfStoryPanel from './PmfStoryPanel';


// Demo-only interpretation constants. Adjust per assay in real use.
const TC_RATIO_THRESHOLD = 0.02; // T/C above this is read as "+"
const C_THRESHOLD = 8.0;         // control line height below this → invalid


// Small label/value row used inside the control-line and test-line cards.
// Phone-friendly: label on the left, value on the right, mono-ish look.
const MetricRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="baseline"
    sx={{ py: 0.25 }}
  >
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Stack>
);


export interface PostScanPageProps {
  /** The most recently completed scan — shown at the top. */
  currentScan: CompletedScan;
  /** All completed scans this session, including currentScan. */
  completedScans: CompletedScan[];
  /** Diagnostics from the most recent scan's worker. */
  pmfDiagnostics: PmfStoryDiagnostics | null;
  /** Called when user deletes a scan from the history list. */
  onDeleteScan: (sessionId: string) => void;
  /** Called when user taps Scan Again. */
  onScanAgain: () => void;
}


const PostScanPage: React.FC<PostScanPageProps> = ({
  currentScan,
  completedScans,
  pmfDiagnostics,
  onDeleteScan,
  onScanAgain,
}) => {

  const [stripIdx, setStripIdx] = useState(0);
  const [showRegions, setShowRegions] = useState(true);
  const [exporting, setExporting] = useState<string | null>(null); // sessionId being exported, or 'all'

  const results = currentScan.strips;
  const analysisMs = currentScan.analysisMs;
  const hScale = currentScan.engineConfig.init_h_scale;
  const isCollectionMode = results.length === 0;

  // Use a fallback strip so all hooks below always receive a stable object.
  // The real guard (early return) sits AFTER useMemo so hooks are never
  // called conditionally — required by the Rules of Hooks.
  const strip = results[stripIdx] ?? results[0];

  // Build chart series + options for the active strip.
  const { series, options } = useMemo<{
    series: ApexOptions['series'];
    options: ApexOptions;
  }>(() => {
    if (!strip) return { series: [], options: {} as ApexOptions };

    const profileXY = strip.profile.map((y, i) => ({ x: i, y }));
    const baselineXY = strip.baseline.map((y, i) => ({ x: i, y }));

    const labelStyle = { fontSize: '11px' };

    const tAnnotations = strip.tPos.map((pos, i) => ({
      x: pos,
      borderColor: '#d32f2f',
      label: {
        text: `T${i + 1}`,
        offsetY: -2,
        style: { color: '#fff', background: '#d32f2f', ...labelStyle },
      },
    }));

    const regions = strip.profileRegions ?? { baseline: [], control: [], test: [] };
    const regionAnnotations = showRegions
      ? [
        ...regions.baseline.map(([s, e]: number[]) => ({
          x: s, x2: e,
          fillColor: '#1976d2',
          opacity: 0.3,
        })),
        ...regions.control.map(([s, e]: number[]) => ({
          x: s, x2: e,
          fillColor: '#2e7d32',
          opacity: 0.3,
        })),
        ...regions.test.map(([s, e]: number[]) => ({
          x: s, x2: e,
          fillColor: '#d32f2f',
          opacity: 0.3,
        })),
      ]
      : [];

    return {
      series: [
        { name: 'Mean Profile', data: profileXY },
        { name: 'Baseline', data: baselineXY },
      ],
      options: {
        chart: {
          toolbar: { show: false },
          animations: { enabled: false },
          zoom: { enabled: false },
        },
        stroke: { width: [2, 1.5], dashArray: [0, 4], curve: 'smooth' },
        colors: ['#1976d2', '#9e9e9e'],
        xaxis: {
          type: 'numeric',
          title: { text: 'Profile' },
          labels: { show: true, formatter: (v) => Number(v).toFixed(0) },
          axisTicks: { show: true },
          tickAmount: 6,
        },
        yaxis: {
          min: 0,
          max: 285,
          tickAmount: 5,
          title: { text: 'Intensity' },
          labels: { formatter: (v) => (v != null ? Number(v).toFixed(0) : '') },
        },
        legend: { position: 'top' },
        annotations: {
          xaxis: [
            ...regionAnnotations,
            {
              x: strip.cPos,
              borderColor: '#2e7d32',
              label: {
                text: 'C',
                offsetY: -2,
                style: { color: '#fff', background: '#2e7d32', ...labelStyle },
              },
            },
            ...tAnnotations,
          ],
          points: showRegions ? (() => {
            const abbrev = strip.tPos.length >= 2;
            return [
              ...regions.baseline.map(([s, e]: number[]) => ({
                x: (s + e) / 2, y: 280,
                marker: { size: 0 },
                label: {
                  text: abbrev ? 'B' : 'Baseline',
                  borderWidth: 0,
                  style: { background: 'transparent', color: '#1565c0', fontSize: '9px', fontWeight: '600' },
                },
              })),
              ...regions.control.map(([s, e]: number[]) => ({
                x: (s + e) / 2, y: 280,
                marker: { size: 0 },
                label: {
                  text: abbrev ? 'C' : 'Control',
                  borderWidth: 0,
                  style: { background: 'transparent', color: '#1b5e20', fontSize: '9px', fontWeight: '600' },
                },
              })),
              ...regions.test.map(([s, e]: number[]) => ({
                x: (s + e) / 2, y: 280,
                marker: { size: 0 },
                label: {
                  text: abbrev ? 'T' : 'Test',
                  borderWidth: 0,
                  style: { background: 'transparent', color: '#b71c1c', fontSize: '9px', fontWeight: '600' },
                },
              })),
            ];
          })() : [],
        },
        tooltip: { enabled: true, x: { formatter: (v) => `${v}` } },
      },
    };
  }, [strip, showRegions]);

  // Export handlers
  async function handleDownloadCurrent() {
    if (exporting) return;
    setExporting(currentScan.sessionId);
    try {
      await downloadScanZip(currentScan);
    } finally {
      setExporting(null);
    }
  }

  async function handleDownloadAll() {
    if (exporting) return;
    setExporting('all');
    try {
      await downloadAllScanZips(completedScans);
    } finally {
      setExporting(null);
    }
  }

  // Guard — placed after all hooks so Rules of Hooks is satisfied.
  if (!strip && !isCollectionMode) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h6" gutterBottom>No results available</Typography>
          <Button variant="contained" onClick={onScanAgain}>Scan Again</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: '100vh',
      '@supports (height: 100dvh)': { height: '100dvh' },
      overflowY: 'auto',
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      bgcolor: 'grey.50',
    }}>
      <Box sx={{ maxWidth: 800, mx: 'auto', px: 2.5, pt: 4, pb: 8 }}>

        {/* ── Brand header ──────────────────────────────────────────────── */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 2, lineHeight: 1 }}
          >
            NovarumDX
          </Typography>
          <Typography variant="h5" fontWeight={600} sx={{ mt: 0.5 }}>Results</Typography>
        </Box>

        {/* Strip navigator — shown only when there are multiple strips */}
        {results.length > 1 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <IconButton
              onClick={() => setStripIdx((i) => i - 1)}
              disabled={stripIdx === 0}
              aria-label="Previous strip"
              size="large"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: 1.5,
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 28 }} />
            </IconButton>

            <Typography variant="body1" fontWeight={700}>
              Strip {stripIdx + 1} of {results.length}
            </Typography>

            <IconButton
              onClick={() => setStripIdx((i) => i + 1)}
              disabled={stripIdx === results.length - 1}
              aria-label="Next strip"
              size="large"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: 1.5,
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Stack>
        )}

        {isCollectionMode ? (
          /* Collection mode result — no analysis data */
          <Card
            elevation={0}
            sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <CardContent>
              <Typography
                variant="caption"
                sx={{
                  display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: 0.8, color: 'text.secondary'
                }}
              >
                Collection Mode Result
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="body2" color="text.secondary">
                This scan was captured in collection mode. Frame images are saved in the ZIP
                but no strip analysis was performed — T/C ratios and control line scores are
                not available.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Profile chart */}
            <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary' }}>
                    Intensity Profile · Strip {stripIdx + 1}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Regions</Typography>
                    <Switch size="small" checked={showRegions} onChange={(_, v) => setShowRegions(v)} />
                  </Stack>
                </Stack>
                <Chart options={options} series={series} type="line" height={260} />
              </CardContent>
            </Card>

            {/* Control line */}
            <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary' }}>
                    Control Line
                  </Typography>
                  <Chip
                    size="small"
                    label={strip!.cHeight < C_THRESHOLD ? 'Invalid' : 'Valid'}
                    color={strip!.cHeight < C_THRESHOLD ? 'default' : 'success'}
                  />
                </Stack>
                <Divider sx={{ mb: 1 }} />
                <MetricRow label="Height (Score)" value={strip!.cHeight.toFixed(4)} />
                <MetricRow label="Area" value={strip!.cArea.toFixed(0)} />
                <MetricRow label="Peak Position" value={strip!.cPos.toFixed(0)} />
              </CardContent>
            </Card>

            {/* Test lines */}
            <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary' }}>
                  Test Lines
                </Typography>
                <Divider sx={{ mb: 1 }} />

                {strip!.cHeight < C_THRESHOLD ? (
                  <Typography variant="body2" color="text.secondary">
                    Test lines aren't interpreted while the control line is invalid.
                  </Typography>
                ) : (
                  strip!.tcRatio.map((ratio, i) => {
                    const positive = ratio > TC_RATIO_THRESHOLD;
                    return (
                      <Box
                        key={i}
                        sx={{
                          mb: i < strip!.tcRatio.length - 1 ? 2 : 0,
                          pb: i < strip!.tcRatio.length - 1 ? 2 : 0,
                          borderBottom: i < strip!.tcRatio.length - 1 ? 1 : 0,
                          borderColor: 'divider',
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 0.5 }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            T{i + 1}
                          </Typography>
                          <Chip
                            size="small"
                            label={positive ? 'Positive' : 'Negative'}
                            color={positive ? 'error' : 'success'}
                          />
                        </Stack>
                        <MetricRow label="T/C ratio" value={ratio.toFixed(4)} />
                        <MetricRow label="Height (Score)" value={strip!.tHeight[i]?.toFixed(4) ?? '—'} />
                        <MetricRow label="Area" value={strip!.tArea[i]?.toFixed(0) ?? '—'} />
                        <MetricRow label="Peak Position" value={strip!.tPos[i]?.toFixed(0) ?? '—'} />
                      </Box>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Post-scan workflow notes */}
        <Card elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary' }}>
              Post-Scan Workflow
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary" component="div">
              In a production deployment the result is typically:
              <ul style={{ marginTop: 4, marginBottom: 4 }}>
                <li>Sent to a central database (manufacturer, health service, EHR / LIMS)</li>
                <li>Stored locally on the phone for offline use</li>
                <li>Displayed to the user — verdict, raw values, or a guided follow-up</li>
              </ul>
              How the result is rendered, archived, and audited is highly assay-
              and customer-specific.
            </Typography>

            {(analysisMs !== null || hScale !== undefined) && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary' }}
                >
                  Performance
                </Typography>
                {analysisMs !== null && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                      <Typography variant="body2">Analysis time</Typography>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                        {analysisMs.toFixed(1)} ms
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Time for the reader's final analysis pass after all frames were collected.
                    </Typography>
                  </Box>
                )}
                {hScale !== undefined && (
                  <Box sx={{ mt: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                      <Typography variant="body2">H Factor</Typography>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                        {hScale.toFixed(2)}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Homography scale override applied for this scan.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* PMF Story — shown when story was captured */}
        {(currentScan.pmfStory?.length ?? 0) > 0 && (
          <PmfStoryPanel
            story={currentScan.pmfStory}
            sessionId={currentScan.sessionId}
            diagnostics={pmfDiagnostics}
            strips={currentScan.strips}
            engineConfig={currentScan.engineConfig}
            testConfig={currentScan.testConfig}
          />
        )}

        {/* ── Export buttons ───────────────────────────────────────────────── */}
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={() => { void handleDownloadCurrent(); }}
            disabled={!!exporting}
            startIcon={exporting === currentScan.sessionId
              ? <CircularProgress size={18} />
              : <DownloadIcon />}
            sx={{ py: 1.5, borderRadius: 1.5, fontWeight: 600 }}
          >
            {exporting === currentScan.sessionId ? 'Building ZIP…' : 'Download ZIP'}
          </Button>

          {completedScans.length > 1 && (
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => { void handleDownloadAll(); }}
              disabled={!!exporting}
              startIcon={exporting === 'all' ? <CircularProgress size={18} /> : <DownloadIcon />}
              sx={{ py: 1.5, borderRadius: 1.5, fontWeight: 600 }}
            >
              {exporting === 'all'
                ? 'Building ZIPs…'
                : `Export All (${completedScans.length} scans)`}
            </Button>
          )}
        </Stack>

        {/* ── Scan history ─────────────────────────────────────────────────── */}
        {completedScans.length > 1 && (
          <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent>
              <Typography
                variant="caption"
                sx={{ display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary' }}
              >
                This Session ({completedScans.length} scans)
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Results are cleared on page reload.
              </Typography>
              <Stack spacing={1}>
                {[...completedScans].reverse().map((scan) => {
                  const isCurrent = scan.sessionId === currentScan.sessionId;
                  const time = new Date(scan.completedAt).toLocaleTimeString([], {
                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                  });
                  return (
                    <Stack
                      key={scan.sessionId}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: isCurrent ? 'primary.50' : 'grey.50',
                        border: '1px solid',
                        borderColor: isCurrent ? 'primary.200' : 'divider',
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={isCurrent ? 600 : 400}>
                          {time}{isCurrent ? ' · current' : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {scan.strips.length} strip{scan.strips.length !== 1 ? 's' : ''}
                          {scan.pmfStory.length > 0
                            ? ` · ${scan.pmfStory.filter((f) => f.pmfStatus === 2).length} fit frames`
                            : ''}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Download ZIP">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!!exporting}
                              onClick={() => {
                                setExporting(scan.sessionId);
                                downloadScanZip(scan).finally(() => setExporting(null));
                              }}
                            >
                              {exporting === scan.sessionId
                                ? <CircularProgress size={16} />
                                : <DownloadIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {!isCurrent && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => onDeleteScan(scan.sessionId)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        )}

        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={onScanAgain}
          sx={{ py: 1.5, borderRadius: 1.5, fontSize: '1rem', fontWeight: 600 }}
        >
          Scan Again
        </Button>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 2 }}
        >
          For demonstration purposes only · Not for clinical use
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
        >
          SDK ndx-imaging-web v{__NDX_SDK_VERSION__}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
        >
          <Link href="https://www.novarumdx.com/" target="_blank" rel="noopener noreferrer">
            novarumdx.com
          </Link>
        </Typography>

      </Box>
    </Box>
  );
};

export default PostScanPage;
